/**
 * The practice range.
 *
 * A solo room with a training dummy in the far stance, no clock and no score.
 * It is deliberately the *same* screen stack as a match — same HUD, same combat
 * input, same room bridge — because the whole point of practising is that the
 * timing you learn here is the timing the duel judges. The only additions are a
 * control strip for the dummy's cadence and a restore button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { net } from '../networking/net';
import { useNet, selectSelf } from '../networking/netStore';
import { Hud } from '../ui/Hud';
import { ConnectionCheck } from '../ui/ConnectionCheck';
import { DebugOverlay } from '../debug/DebugOverlay';
import { useCombatInput } from '../gameplay/useCombatInput';
import { useRoomStateMirror, useServerEvents } from '../gameplay/roomBridge';
import { useSettings } from '../gameplay/settingsStore';
import { resetScene } from '../scene/sceneBus';
import { PracticePanel } from './PracticePanel';

export interface PracticeScreenProps {
  onLeave: () => void;
}

export function PracticeScreen({ onLeave }: PracticeScreenProps) {
  const phase = useNet((s) => s.phase);
  const fatalError = useNet((s) => s.fatalError);
  const intervalMs = useNet((s) => s.practice.intervalMs);
  const self = useNet(selectSelf);
  const showDebug = useSettings((s) => s.showDebug);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [locked, setLocked] = useState(false);

  const combatActive = phase === 'round' || phase === 'countdown';
  const input = useCombatInput(canvas, combatActive);
  const inputRef = useRef(input);
  inputRef.current = input;

  /* --- bind to the shared canvas -------------------------------------- */
  useEffect(() => {
    setCanvas(document.getElementById('spell-canvas') as HTMLCanvasElement | null);
  }, []);

  /* --- scene mode ------------------------------------------------------ */
  useEffect(() => {
    resetScene('practice');
    return () => resetScene('idle');
  }, []);

  /* --- room -> interface ------------------------------------------------ */
  useRoomStateMirror();
  useServerEvents();

  /**
   * The control strip fades while the pointer is locked so it cannot compete
   * with the reticle, and comes back the moment the player releases the mouse.
   */
  useEffect(() => {
    const onChange = () => setLocked(document.pointerLockElement !== null);
    onChange();
    document.addEventListener('pointerlockchange', onChange);
    return () => document.removeEventListener('pointerlockchange', onChange);
  }, []);

  const leave = useCallback(() => {
    void net.leave();
    useNet.getState().reset();
    onLeave();
  }, [onLeave]);

  if (fatalError) {
    return (
      <div className="screen">
        <div className="panel" style={{ maxWidth: 480 }}>
          <h2 className="title" style={{ fontSize: 24 }}>연결 오류</h2>
          <p className="subtitle">{fatalError}</p>
          <button type="button" className="btn btn--primary" onClick={leave}>
            로비로
          </button>
        </div>
      </div>
    );
  }

  /* Before the first round: the same device check a match uses, minus the
     halves of it that are about an opponent who does not exist here. */
  if (phase === 'check' || phase === 'waiting') {
    return <ConnectionCheck practice onLeave={leave} />;
  }

  return (
    <>
      {combatActive ? <Hud /> : null}

      <div className="overlay">
        <PracticePanel playing={locked} onLeave={leave} />
      </div>

      {combatActive && !locked ? (
        <div
          className="screen"
          style={{ background: 'rgba(4,6,14,0.35)', cursor: 'pointer' }}
          onClick={() => inputRef.current.requestLock()}
          data-testid="click-to-play"
        >
          <div className="panel" style={{ textAlign: 'center', maxWidth: 560 }}>
            <div className="title" style={{ fontSize: 20 }}>클릭해서 조작 시작</div>
            <div className="hint">
              마우스 이동 = 시선 · <b>WASD</b> = 이동 · <b>아무 마우스 버튼이나 스페이스를 누른 채로</b> = 시전
              <br />
              누른 채로 <b>영창을 외치고 문양을 그린 뒤 손을 뗍니다</b>. 시전 중 <b>Shift</b>로 획을 나눕니다.
              <br />
              훈련 인형은 쏘는 순간의 위치를 노립니다 — <b>쏜 뒤에 비켜서면 피할 수 있습니다</b>.
              <br />
              주문을 고르지 않습니다 — 무엇을 외치고 무엇을 그렸는지로 결정됩니다.
            </div>
            {/* Said separately and last: this is the state the player is in
                right now, and the panel is live *while this is on screen*. */}
            <div className="hint" style={{ marginTop: 10, color: 'var(--warn)' }}>
              지금은 마우스가 풀려 있습니다 — <b>오른쪽 아래 패널에서 탄속과 간격을 바꿀 수 있어요.</b>
              <br />
              전투 중에 다시 바꾸려면 <b>ESC</b>로 마우스를 푸세요.
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              {self?.usingFallbackInput
                ? '음성 대신 키보드로 영창을 입력하고 있습니다 — HUD 아래 입력란을 사용하세요.'
                : `훈련 인형은 ${(intervalMs / 1000).toFixed(1)}초마다 공격합니다.`}
            </div>
          </div>
        </div>
      ) : null}

      {showDebug ? <DebugOverlay /> : null}
    </>
  );
}
